import classnames from 'classnames';
import * as React from 'react';
import { memo, useCallback } from 'react';
import { connect } from 'react-redux';
import { Action } from 'redux-actions';

import { Segment as SegmentModel } from '../../../schemas/Segment';
import { SearchFormState } from '../../../store/reducers';
import { addSegment, removeSegment } from '../../../store/segments/actions';
import { canAddSegment, canRemoveSegment, isMultiCity, getSegments } from '../../../store/segments/selectors';
import Segment from './Segment/Segment';
import Actions from './Actions/Actions';
import { useTheme } from '../../../../theme';
import { useConfig } from '../../../../context';
import { useForm } from 'react-final-form';
import MediaQuery from 'react-responsive';
import { MOBILE_MIN_WIDTH } from '../../../../utils';

interface StateProps {
	isMultiCity: boolean;
	canAddSegment: boolean;
	canRemoveSegment: boolean;
	segments: SegmentModel[];
}

interface DispatchProps {
	addSegment: () => Action<null>;
	removeSegment: (segmentIndex: number) => Action<number>;
}

type Props = StateProps & DispatchProps;

const Segments = memo<Props>(
	({ segments, isMultiCity, canAddSegment, canRemoveSegment, addSegment, removeSegment }) => {
		const { Segments: css } = useTheme('SearchForm');

		const { pauseValidation } = useForm();

		const showAddSegment = isMultiCity && canAddSegment;
		const showRemoveSegment = canRemoveSegment;
		const showActionsBlock = showAddSegment || showRemoveSegment;
		const alternative = useConfig().SearchForm.mobileFormType !== 'common';
		const proMode = useConfig().SearchForm.proMode;

		const removeLastSegment = useCallback(() => removeSegment(segments.length - 1), [segments]);

		const onAddSegmentHandler = () => {
			if (canAddSegment) {
				addSegment();
				pauseValidation();
			}
		};

		return (
			<div
				className={classnames(css.segments, {
					[css.segments_multiCity]: isMultiCity,
					[css.alternative]: alternative
				})}
			>
				<div>
					{segments.map((segment, index) => (
						<Segment key={index} segmentId={index} />
					))}
				</div>

				{!proMode && showActionsBlock && (
					<MediaQuery maxWidth={MOBILE_MIN_WIDTH}>
						<Actions
							isAlternativeView={alternative}
							canRemoveSegment={canRemoveSegment}
							onAddSegment={onAddSegmentHandler}
							removeLastSegment={removeLastSegment}
							showAddSegment={showAddSegment}
							showRemoveSegment={showRemoveSegment}
						/>
					</MediaQuery>
				)}
			</div>
		);
	},
	(prevProps, nextProps) =>
		prevProps.isMultiCity === nextProps.isMultiCity && prevProps.segments.length === nextProps.segments.length
);

const mapStateToProps = (state: SearchFormState): StateProps => {
	return {
		isMultiCity: isMultiCity(state),
		canAddSegment: canAddSegment(state),
		canRemoveSegment: canRemoveSegment(state),
		segments: getSegments(state)
	};
};

const mapDispatchToProps: DispatchProps = {
	addSegment,
	removeSegment
};

export default connect<StateProps, DispatchProps, {}, SearchFormState>(mapStateToProps, mapDispatchToProps)(Segments);
